export const BUILDER_STYLE_CLIPBOARD_MIXED_VALUE = "__mixed__";

export const BUILDER_TEXT_STYLE_KEYS = [
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "lineHeight",
  "textAlign",
  "textColor",
  "textDecoration",
] as const;

export const BUILDER_COLOR_STYLE_KEYS = [
  "textColor",
  "backgroundColor",
  "borderColor",
] as const;

export const BUILDER_SAFE_VISUAL_STYLE_KEYS = [
  ...BUILDER_TEXT_STYLE_KEYS,
  "letterSpacing",
  "backgroundColor",
  "backgroundImage",
  "borderColor",
  "borderWidth",
  "borderRadius",
  "boxShadow",
  "backdropFilter",
  "opacity",
] as const;

export type BuilderTextStyleKey = typeof BUILDER_TEXT_STYLE_KEYS[number];
export type BuilderColorStyleKey = typeof BUILDER_COLOR_STYLE_KEYS[number];
export type BuilderStyleClipboardKey =
  typeof BUILDER_SAFE_VISUAL_STYLE_KEYS[number];
export type BuilderStyleClipboardMode = "color" | "text" | "visual";
export type BuilderStyleClipboardSource = "block" | "inlineText";

export type BuilderStyleClipboard = {
  mode: BuilderStyleClipboardMode;
  source: BuilderStyleClipboardSource;
  values: Partial<Record<BuilderStyleClipboardKey, string>>;
};

type BuilderStyleSource =
  | Readonly<Record<string, unknown>>
  | null
  | undefined;

const safeVisualStyleKeySet = new Set<string>(
  BUILDER_SAFE_VISUAL_STYLE_KEYS,
);

function normalizedStyleValue(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();

  return normalized !== ""
    && normalized !== BUILDER_STYLE_CLIPBOARD_MIXED_VALUE
    ? normalized
    : null;
}

function filteredStyleValues(
  source: BuilderStyleSource,
  allowedKeys: readonly BuilderStyleClipboardKey[],
): Partial<Record<BuilderStyleClipboardKey, string>> {
  if (!source) {
    return {};
  }

  return Object.fromEntries(allowedKeys.flatMap((key) => {
    const value = normalizedStyleValue(source[key]);

    return value === null ? [] : [[key, value]];
  })) as Partial<Record<BuilderStyleClipboardKey, string>>;
}

function createBuilderStyleClipboard(
  mode: BuilderStyleClipboardMode,
  source: BuilderStyleClipboardSource,
  values: BuilderStyleSource,
  allowedKeys: readonly BuilderStyleClipboardKey[],
): BuilderStyleClipboard | null {
  const safeValues = filteredStyleValues(values, allowedKeys);

  return Object.keys(safeValues).length > 0
    ? {
        mode,
        source,
        values: safeValues,
      }
    : null;
}

export function createBuilderTextColorClipboard(
  styles: BuilderStyleSource,
): BuilderStyleClipboard | null {
  return createBuilderStyleClipboard(
    "color",
    "inlineText",
    styles,
    ["textColor"],
  );
}

export function createBuilderTextStyleClipboard(
  styles: BuilderStyleSource,
): BuilderStyleClipboard | null {
  return createBuilderStyleClipboard(
    "text",
    "inlineText",
    styles,
    BUILDER_TEXT_STYLE_KEYS,
  );
}

export function createBuilderBlockColorClipboard(
  styles: BuilderStyleSource,
): BuilderStyleClipboard | null {
  return createBuilderStyleClipboard(
    "color",
    "block",
    styles,
    BUILDER_COLOR_STYLE_KEYS,
  );
}

export function createBuilderVisualStyleClipboard(
  styles: BuilderStyleSource,
): BuilderStyleClipboard | null {
  return createBuilderStyleClipboard(
    "visual",
    "block",
    styles,
    BUILDER_SAFE_VISUAL_STYLE_KEYS,
  );
}

export function builderStyleClipboardValuesForDestination(
  clipboard: BuilderStyleClipboard | null | undefined,
  allowedKeys: readonly string[],
): Partial<Record<BuilderStyleClipboardKey, string>> {
  if (!clipboard) {
    return {};
  }

  const destinationKeySet = new Set<string>(allowedKeys);

  return Object.fromEntries(
    Object.entries(clipboard.values).flatMap(([key, rawValue]) => {
      if (!safeVisualStyleKeySet.has(key) || !destinationKeySet.has(key)) {
        return [];
      }

      const value = normalizedStyleValue(rawValue);

      return value === null ? [] : [[key, value]];
    }),
  ) as Partial<Record<BuilderStyleClipboardKey, string>>;
}
